import { Avatar } from "@circleco/compass/components/Avatar";
import { Typography } from "@circleco/compass/components/Typography";

import { ProfileHoverCard } from "@/react/components/Profile/HoverCard";
import { REACTION_PRESENTATION } from "@/react/components/Reactions/PostCommentReactionAction/constants";
import { isIntendedOnNewTab } from "@/react/helpers/browserHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useProfileLinkProps } from "@/react/hooks/profile/useProfileLinkProps";
import type { CommentLikeMember } from "@/react/lib/queries/commentLikes";

interface MemberRowProps {
  className?: string;
  communityMember: CommentLikeMember;
  onNavigate: () => void;
  showReactionBadge?: boolean;
}

export const MemberRow = ({
  className,
  communityMember,
  onNavigate,
  showReactionBadge = false,
}: MemberRowProps) => {
  const { onClick, href, ...profileLinkProps } = useProfileLinkProps({
    public_uid: communityMember.public_uid,
  });
  const sentiment =
    showReactionBadge && communityMember.reaction_type
      ? REACTION_PRESENTATION[communityMember.reaction_type].sentiment
      : null;

  return (
    <a
      className={classNames("flex items-center gap-3", className)}
      href={href}
      onClick={event => {
        if (!isIntendedOnNewTab(event)) {
          onNavigate();
        }
        onClick?.(event);
      }}
      {...profileLinkProps}
    >
      <div className="shrink-0">
        <ProfileHoverCard
          communityMemberId={communityMember.id}
          name={communityMember.name}
          publicUid={communityMember.public_uid}
          headline={communityMember.headline}
          avatarURL={communityMember.avatar_url}
        >
          {sentiment ? (
            <Avatar
              size="lg"
              name={communityMember.name}
              src={communityMember.avatar_url}
              sentiment={sentiment}
            />
          ) : (
            <Avatar
              size="lg"
              name={communityMember.name}
              src={communityMember.avatar_url}
            />
          )}
        </ProfileHoverCard>
      </div>
      <div
        className={classNames(
          "flex min-h-10 min-w-0 flex-1 flex-col",
          !communityMember.headline && "justify-center",
        )}
      >
        <Typography variant="label-sm" truncate>
          {communityMember.name}
        </Typography>
        {communityMember.headline && (
          <Typography variant="body-sm" color="secondary" truncate>
            {communityMember.headline}
          </Typography>
        )}
      </div>
    </a>
  );
};
